import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  collection, addDoc, updateDoc, deleteDoc, doc, query, where,
  onSnapshot, serverTimestamp, orderBy, getDoc, arrayUnion
} from 'firebase/firestore';
import { signOut } from 'firebase/auth';
import { auth, db } from './firebase';
import { useTrackerData } from './hooks/useTrackerData';
import {
  nextIncidentNumber
} from './services/applicationCounterService';
import { processIncidentTriggers } from './services/incidentLinkingService';
import { formatDisplayNumber } from './utils/helpers';
import LoadingSpinner from './components/LoadingSpinner';
import IncidentReportBoard from './IncidentReportBoard';
import './IncidentReportsApp.css';

const STATUS_OPTIONS = ['Open', 'Under Investigation', 'Resolved', 'Closed'];

function IncidentReportsApp() {
  const navigate = useNavigate();
  const { user, userProfile, authLoading, applications, role, isAdmin } = useTrackerData();

  const [incidents, setIncidents] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [editingIncident, setEditingIncident] = useState(null);
  const [viewOnly, setViewOnly] = useState(false);
  const [saving, setSaving] = useState(false);

  // Filters
  const [filterStatus, setFilterStatus] = useState('All');
  const [filterApp, setFilterApp] = useState('All');
  const [searchQuery, setSearchQuery] = useState('');
  const [sortBy, setSortBy] = useState('displayNumber');
  const [sortOrder, setSortOrder] = useState('desc');

  // Form state
  const blankForm = () => ({
    title: '',
    incidentDateTime: new Date().toISOString().slice(0, 16), // local "YYYY-MM-DDTHH:mm"
    subject: '',
    reportedBy: user?.email || '',
    location: '',
    triggersInjury: false,
    triggersSopViolation: false,
    triggersMaintenance: false,
    status: 'Open',
    description: '',
    applicationName: ''
  });
  const [formData, setFormData] = useState(blankForm());
  const [newUpdateText, setNewUpdateText] = useState('');

  useEffect(() => {
    document.title = 'Incident Reports - Wixon & Cross Desk Suite';
  }, []);

  // When user becomes available, populate reportedBy default
  useEffect(() => {
    if (user && !editingIncident) {
      setFormData(f => ({ ...f, reportedBy: f.reportedBy || user.email }));
    }
  }, [user, editingIncident]);

  // Incidents listener
  useEffect(() => {
    if (!user) return;
    const q = query(collection(db, 'incidents'), orderBy('createdAt', 'desc'));
    const unsub = onSnapshot(q, (snap) => {
      setIncidents(snap.docs.map(d => ({ id: d.id, ...d.data() })));
    });
    return () => unsub();
  }, [user]);

  const openCreateForm = () => {
    setEditingIncident(null);
    setViewOnly(false);
    setFormData(blankForm());
    setShowForm(true);
  };

  const openEditForm = (incident) => {
    // Determine if this user can edit this incident or only view it
    const canEditThis = incident.createdBy === user.uid || isAdmin;
    setViewOnly(!canEditThis);
    setEditingIncident(incident);
    setFormData({
      title: incident.title || '',
      incidentDateTime: incident.incidentDateTime
        ? toLocalDatetimeInput(incident.incidentDateTime)
        : '',
      subject: incident.subject || '',
      reportedBy: incident.reportedBy || '',
      location: incident.location || '',
      triggersInjury: !!incident.triggersInjury,
      triggersSopViolation: !!incident.triggersSopViolation,
      triggersMaintenance: !!incident.triggersMaintenance,
      status: incident.status || 'Open',
      description: incident.description || '',
      applicationName: incident.applicationName || ''
    });
    setShowForm(true);
  };

  const closeForm = () => {
    setShowForm(false);
    setEditingIncident(null);
    setViewOnly(false);
    setFormData(blankForm());
    setNewUpdateText('');
  };

  const handleSave = async (e) => {
    e.preventDefault();
    if (!formData.title.trim() || !formData.applicationName) {
      alert('Title and Application are required.');
      return;
    }
    setSaving(true);

    try {
      const dataToSave = {
        title: formData.title.trim(),
        incidentDateTime: formData.incidentDateTime
          ? new Date(formData.incidentDateTime)
          : null,
        subject: formData.subject.trim(),
        reportedBy: formData.reportedBy.trim() || user.email,
        location: formData.location.trim(),
        triggersInjury: !!formData.triggersInjury,
        triggersSopViolation: !!formData.triggersSopViolation,
        triggersMaintenance: !!formData.triggersMaintenance,
        status: formData.status,
        description: formData.description,
        applicationName: formData.applicationName,
        updatedAt: serverTimestamp(),
        updatedBy: user.email
      };

      let incidentId, previousTriggers;

      if (editingIncident) {
        previousTriggers = {
          injury: !!editingIncident.triggersInjury,
          maintenance: !!editingIncident.triggersMaintenance,
          sopViolation: !!editingIncident.triggersSopViolation
        };
        await updateDoc(doc(db, 'incidents', editingIncident.id), dataToSave);
        incidentId = editingIncident.id;
      } else {
        // Reserve sequential number
        let displayNumber = null;
        try {
          displayNumber = await nextIncidentNumber(formData.applicationName);
        } catch (err) {
          console.error('Could not reserve incident number:', err);
        }

        const docRef = await addDoc(collection(db, 'incidents'), {
          ...dataToSave,
          ...(displayNumber !== null ? { displayNumber } : {}),
          updates: [],
          createdAt: serverTimestamp(),
          createdBy: user.uid,
          createdByEmail: user.email
        });
        incidentId = docRef.id;
        previousTriggers = null;
      }

      // Process trigger checkboxes (cross-app linking)
      await processIncidentTriggers(
        { id: incidentId, ...dataToSave },
        previousTriggers,
        user
      );

      closeForm();
    } catch (err) {
      console.error('Error saving incident:', err);
      alert('Error saving incident: ' + err.message);
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (incident) => {
    if (!window.confirm(`Delete incident "${incident.title}"? This cannot be undone.`)) return;
    try {
      // Cancel related records first (audit-friendly): treat the deletion as
      // transitioning every trigger from its current value to false.
      await processIncidentTriggers(
        {
          id: incident.id,
          triggersInjury: false,
          triggersMaintenance: false,
          triggersSopViolation: false
        },
        {
          injury: !!incident.triggersInjury,
          maintenance: !!incident.triggersMaintenance,
          sopViolation: !!incident.triggersSopViolation
        },
        user
      );

      await deleteDoc(doc(db, 'incidents', incident.id));
    } catch (err) {
      console.error('Error deleting incident:', err);
      alert('Error deleting incident.');
    }
  };

  const handleAddUpdate = async (incident) => {
    const text = newUpdateText.trim();
    if (!text) return;
    try {
      await updateDoc(doc(db, 'incidents', incident.id), {
        updates: arrayUnion({
          // Note: serverTimestamp() can't be used inside arrayUnion;
          // fall back to client time.
          timestamp: new Date(),
          description: text,
          userId: user.uid,
          userEmail: user.email
        }),
        updatedAt: serverTimestamp(),
        updatedBy: user.email
      });
      setNewUpdateText('');
      // Refresh the editing object so the new update is visible immediately.
      const fresh = await getDoc(doc(db, 'incidents', incident.id));
      if (fresh.exists()) {
        setEditingIncident({ id: fresh.id, ...fresh.data() });
      }
    } catch (err) {
      console.error('Error adding update:', err);
      alert('Could not add update.');
    }
  };

  // ── Filtering & sorting ──────────────────────────────────────────────────
  const filteredIncidents = incidents.filter(i => {
    if (filterStatus !== 'All' && i.status !== filterStatus) return false;
    if (filterApp !== 'All' && i.applicationName !== filterApp) return false;
    if (searchQuery) {
      const q = searchQuery.toLowerCase();
      const hay = [i.title, i.subject, i.description, i.location, i.reportedBy]
        .filter(Boolean).join(' ').toLowerCase();
      if (!hay.includes(q)) return false;
    }
    return true;
  });

  filteredIncidents.sort((a, b) => {
    let aVal, bVal;
    if (sortBy === 'createdAt' || sortBy === 'updatedAt') {
      aVal = a[sortBy]?.toDate?.() || new Date(0);
      bVal = b[sortBy]?.toDate?.() || new Date(0);
    } else if (sortBy === 'incidentDateTime') {
      aVal = a.incidentDateTime?.toDate?.() || new Date(0);
      bVal = b.incidentDateTime?.toDate?.() || new Date(0);
    } else if (sortBy === 'displayNumber') {
      aVal = typeof a.displayNumber === 'number' ? a.displayNumber : Number.POSITIVE_INFINITY;
      bVal = typeof b.displayNumber === 'number' ? b.displayNumber : Number.POSITIVE_INFINITY;
    } else {
      aVal = (a[sortBy] || '').toString().toLowerCase();
      bVal = (b[sortBy] || '').toString().toLowerCase();
    }
    if (aVal < bVal) return sortOrder === 'asc' ? -1 : 1;
    if (aVal > bVal) return sortOrder === 'asc' ? 1 : -1;
    return 0;
  });

  const handleSignOut = async () => {
    try { await signOut(auth); navigate('/'); }
    catch (err) { console.error(err); }
  };

  if (authLoading) return <LoadingSpinner message="Loading..." />;
  if (!user || !userProfile) return <LoadingSpinner message="Setting up..." />;

  return (
    <div className="incident-reports-app">
      <header className="header">
        <div className="header-content">
          <div className="breadcrumb-nav-app">
            <button className="breadcrumb-item" onClick={() => navigate('/desktop')}>
              🖥️ Desktop
            </button>
            <span className="breadcrumb-separator">›</span>
            <button className="breadcrumb-item" onClick={() => navigate('/dashboard')}>
              📦 Dashboard
            </button>
            <span className="breadcrumb-separator">›</span>
            <span className="breadcrumb-item breadcrumb-current">🚨 Incident Reports</span>
          </div>
          <div className="header-actions">
            <div className="user-info-section">
              <span className="user-email">{user.email}</span>
              <span className="user-role-label">Role: <span className="role-badge">{role}</span></span>
            </div>
            <button className="btn btn-secondary" onClick={handleSignOut}>Sign Out</button>
          </div>
        </div>
      </header>

      <main className="incidents-main">
        <div className="incidents-toolbar">
          <h2>Incident Reports</h2>
          <button className="btn btn-primary" onClick={openCreateForm}>
            + New Incident
          </button>
        </div>

        {/* Filters */}
        <div className="filter-bar">
          <input
            type="text"
            className="search-input"
            placeholder="Search incidents..."
            value={searchQuery}
            onChange={e => setSearchQuery(e.target.value)}
          />
          <select value={filterStatus} onChange={e => setFilterStatus(e.target.value)} className="filter-select">
            <option value="All">All Statuses</option>
            {STATUS_OPTIONS.map(s => <option key={s} value={s}>{s}</option>)}
          </select>
          <select value={filterApp} onChange={e => setFilterApp(e.target.value)} className="filter-select">
            <option value="All">All Applications</option>
            {applications.map(a => <option key={a.id} value={a.name}>{a.name}</option>)}
          </select>
          <select value={sortBy} onChange={e => setSortBy(e.target.value)} className="filter-select">
            <option value="displayNumber">Number (e.g. WP-I.1)</option>
            <option value="incidentDateTime">Incident Date</option>
            <option value="createdAt">Created Date</option>
            <option value="updatedAt">Updated Date</option>
            <option value="status">Status</option>
            <option value="title">Title</option>
          </select>
          <select value={sortOrder} onChange={e => setSortOrder(e.target.value)} className="filter-select">
            <option value="desc">Descending</option>
            <option value="asc">Ascending</option>
          </select>
        </div>

        <div className="incidents-stats">
          {STATUS_OPTIONS.map(s => (
            <div key={s} className="stat-pill">
              <span className="stat-label">{s}</span>
              <span className="stat-value">
                {filteredIncidents.filter(i => i.status === s).length}
              </span>
            </div>
          ))}
          <div className="stat-pill stat-pill-total">
            <span className="stat-label">Total</span>
            <span className="stat-value">{filteredIncidents.length}</span>
          </div>
        </div>

        <IncidentReportBoard
          incidents={filteredIncidents}
          user={user}
          onEdit={openEditForm}
          onDelete={handleDelete}
          isAdmin={isAdmin}
        />
      </main>

      {/* Form modal */}
      {showForm && (
        <div className="modal-overlay" onClick={closeForm}>
          <div className="modal-content modal-large" onClick={e => e.stopPropagation()}>
            <h2>
              {!editingIncident && 'New Incident Report'}
              {editingIncident && viewOnly && 'View Incident (Read-Only)'}
              {editingIncident && !viewOnly && 'Edit Incident'}
            </h2>
            {editingIncident && typeof editingIncident.displayNumber === 'number' && (
              <p className="incident-number-display">
                {formatDisplayNumber(editingIncident, 'I')}
              </p>
            )}
            {viewOnly && (
              <p className="view-only-note">
                👁️ You don't have permission to edit this incident. Read-only view.
              </p>
            )}
            <form onSubmit={handleSave}>
              {/* fieldset disabled={true} cleanly disables every input/select/textarea inside it */}
              <fieldset disabled={viewOnly} className="form-fieldset">
              <div className="form-row">
                <div className="form-group form-group-full">
                  <label>Incident Title *</label>
                  <input
                    type="text"
                    value={formData.title}
                    onChange={e => setFormData({ ...formData, title: e.target.value })}
                    required
                  />
                </div>
              </div>

              <div className="form-row">
                <div className="form-group">
                  <label>Application *</label>
                  <select
                    value={formData.applicationName}
                    onChange={e => setFormData({ ...formData, applicationName: e.target.value })}
                    required
                  >
                    <option value="">-- Select --</option>
                    {applications.map(a => (
                      <option key={a.id} value={a.name}>{a.name}</option>
                    ))}
                  </select>
                </div>
                <div className="form-group">
                  <label>Current Status</label>
                  <select
                    value={formData.status}
                    onChange={e => setFormData({ ...formData, status: e.target.value })}
                  >
                    {STATUS_OPTIONS.map(s => <option key={s} value={s}>{s}</option>)}
                  </select>
                </div>
              </div>

              <div className="form-row">
                <div className="form-group">
                  <label>Incident Date & Time</label>
                  <input
                    type="datetime-local"
                    value={formData.incidentDateTime}
                    onChange={e => setFormData({ ...formData, incidentDateTime: e.target.value })}
                  />
                </div>
                <div className="form-group">
                  <label>Reported By</label>
                  <input
                    type="text"
                    value={formData.reportedBy}
                    onChange={e => setFormData({ ...formData, reportedBy: e.target.value })}
                  />
                </div>
              </div>

              <div className="form-row">
                <div className="form-group form-group-full">
                  <label>Location of Incident</label>
                  <input
                    type="text"
                    value={formData.location}
                    onChange={e => setFormData({ ...formData, location: e.target.value })}
                  />
                </div>
              </div>

              <div className="form-row">
                <div className="form-group form-group-full">
                  <label>Subject (Brief Description)</label>
                  <input
                    type="text"
                    value={formData.subject}
                    onChange={e => setFormData({ ...formData, subject: e.target.value })}
                  />
                </div>
              </div>

              <div className="form-row">
                <div className="form-group form-group-full">
                  <label>Detailed Description</label>
                  <textarea
                    rows={5}
                    value={formData.description}
                    onChange={e => setFormData({ ...formData, description: e.target.value })}
                  />
                </div>
              </div>

              <fieldset className="triggers-fieldset">
                <legend>Triggers</legend>
                <p className="triggers-help">
                  Checking a box auto-creates a related record in the corresponding app.
                  Unchecking will cancel (not delete) that record.
                </p>
                <label className="checkbox-label">
                  <input
                    type="checkbox"
                    checked={formData.triggersInjury}
                    onChange={e => setFormData({ ...formData, triggersInjury: e.target.checked })}
                  />
                  Triggers Injury Report
                </label>
                <label className="checkbox-label">
                  <input
                    type="checkbox"
                    checked={formData.triggersSopViolation}
                    onChange={e => setFormData({ ...formData, triggersSopViolation: e.target.checked })}
                  />
                  Triggers SOP Violation
                </label>
                <label className="checkbox-label">
                  <input
                    type="checkbox"
                    checked={formData.triggersMaintenance}
                    onChange={e => setFormData({ ...formData, triggersMaintenance: e.target.checked })}
                  />
                  Triggers Maintenance Request
                </label>
              </fieldset>

              {/* Updates section — only on edit */}
              {editingIncident && (
                <div className="updates-section">
                  <h3>Updates</h3>
                  <div className="updates-list">
                    {(editingIncident.updates || []).length === 0 && (
                      <p className="updates-empty">No updates yet.</p>
                    )}
                    {(editingIncident.updates || []).map((u, idx) => (
                      <div key={idx} className="update-item">
                        <div className="update-meta">
                          <span className="update-author">{u.userEmail}</span>
                          <span className="update-time">
                            {u.timestamp?.toDate?.().toLocaleString() ||
                              new Date(u.timestamp).toLocaleString()}
                          </span>
                        </div>
                        <div className="update-text">{u.description}</div>
                      </div>
                    ))}
                  </div>
                  {!viewOnly && (
                    <div className="update-add-row">
                      <textarea
                        rows={2}
                        placeholder="Add an update..."
                        value={newUpdateText}
                        onChange={e => setNewUpdateText(e.target.value)}
                      />
                      <button
                        type="button"
                        className="btn btn-secondary"
                        onClick={() => handleAddUpdate(editingIncident)}
                        disabled={!newUpdateText.trim()}
                      >
                        Add Update
                      </button>
                    </div>
                  )}
                </div>
              )}
              </fieldset>

              <div className="modal-actions">
                {!viewOnly && (
                  <button type="submit" className="btn btn-primary" disabled={saving}>
                    {saving ? 'Saving...' : (editingIncident ? 'Save Changes' : 'Create Incident')}
                  </button>
                )}
                <button type="button" className="btn btn-secondary" onClick={closeForm}>
                  {viewOnly ? 'Close' : 'Cancel'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}

// Convert a Firestore Timestamp or Date to "YYYY-MM-DDTHH:mm" for datetime-local
function toLocalDatetimeInput(value) {
  let d;
  if (value?.toDate) d = value.toDate();
  else if (value instanceof Date) d = value;
  else d = new Date(value);
  if (isNaN(d.getTime())) return '';
  const pad = n => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
}

export default IncidentReportsApp;
